import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import AutoAwesomeMosaicRounded from '@mui/icons-material/AutoAwesomeMosaicRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import ClearRounded from '@mui/icons-material/ClearRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import DoneAllRounded from '@mui/icons-material/DoneAllRounded';

export interface OverviewInstance {
  url: string;
  view?: string;
  stage?: string;
  instanceNumber?: string | number;
  seriesNumber?: string | number;
}

interface StudyOverviewProps {
  open: boolean;
  onClose: () => void;
  instances: OverviewInstance[];
  getPlaybackRate?: (url: string) => number;
}

const NAVY = '#00101f';
const PANEL = '#0b1b2c';
const TILE_BG = '#04121f';
const HAIRLINE = 'rgba(122,160,199,0.22)';
const ACCENT = '#2dd4bf';
// Amber = machine suggestion awaiting a human. Never used for anything confirmed.
const SUGGEST = '#f59e0b';

/**
 * The nine views a cardiologist wants to see at a glance, in Dr Shashi's own order
 * (via Nikhil, 12 Aug). This IS the clinical spec — the grid is fixed at nine so the
 * layout is the same on every study and the eye learns where each view lives.
 *
 * `tags` are the DICOM View Name values (0008,2127) that map to each slot. `needsColour`
 * marks the two slots that are only meaningful with colour Doppler running.
 */
interface OverviewSlot {
  key: string;
  label: string;
  detail: string;
  tags: string[];
  needsColour?: boolean;
}

const SLOTS: OverviewSlot[] = [
  { key: 'plax', label: 'PLAX', detail: 'Parasternal long axis', tags: ['PLAX'] },
  {
    key: 'plax-colour',
    label: 'PLAX colour',
    detail: 'Parasternal long axis, colour',
    tags: ['PLAX'],
    needsColour: true,
  },
  { key: 'sax-av', label: 'SAX — AV', detail: 'Short axis, aortic valve', tags: ['ASX', 'SAX-AV'] },
  {
    key: 'sax-mid',
    label: 'SAX — mid',
    detail: 'Short axis, papillary / mid segment',
    tags: ['SAX-PM', 'SAX'],
  },
  { key: 'ap4', label: 'AP4', detail: 'Apical 4 chamber', tags: ['4CH'] },
  { key: 'ap3', label: 'AP3', detail: 'Apical 3 chamber', tags: ['APLAX', '3CH'] },
  { key: 'ap2', label: 'AP2', detail: 'Apical 2 chamber', tags: ['2CH'] },
  {
    key: 'subcostal-ias',
    label: 'Subcostal colour',
    detail: 'Interatrial septum, colour',
    tags: ['SUBCOSTAL', 'SC'],
    needsColour: true,
  },
  { key: 'ivc', label: 'IVC', detail: 'Subcostal IVC', tags: ['IVC'] },
];

const hasView = (inst: OverviewInstance) =>
  !!inst.view && inst.view.trim() !== '' && inst.view.toUpperCase() !== 'NA';

/**
 * Does this clip contain colour Doppler?
 *
 * There is no metadata for it on our side — the express viewer receives JPEG/WebM with
 * colour already burned into the pixels — so it is measured from the pixels themselves:
 * greyscale ultrasound has R=G=B, a colour jet does not. A frame is sampled on a small
 * canvas and the fraction of pixels with meaningful chroma decides it.
 *
 * Returns null when the answer cannot be obtained (a cross-origin frame taints the canvas
 * and `getImageData` throws). Null means UNKNOWN, never "no colour" — a wrong negative
 * would quietly hide the two colour views from the grid.
 */
const COLOUR_SATURATION_MIN = 40; // 0–255 spread between max and min channel
const COLOUR_PIXEL_FRACTION = 0.02; // 2% of sampled pixels is a jet, not a stray artefact

function detectColour(url: string): Promise<boolean | null> {
  return new Promise(resolve => {
    const finish = (el: HTMLVideoElement | HTMLImageElement, w: number, h: number) => {
      try {
        const canvas = document.createElement('canvas');
        const SAMPLE = 96;
        canvas.width = SAMPLE;
        canvas.height = SAMPLE;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx || !w || !h) {
          resolve(null);
          return;
        }
        ctx.drawImage(el as CanvasImageSource, 0, 0, SAMPLE, SAMPLE);
        const { data } = ctx.getImageData(0, 0, SAMPLE, SAMPLE);
        let coloured = 0;
        let counted = 0;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          // Near-black is the sector surround and carries no information either way.
          if (r < 18 && g < 18 && b < 18) {
            continue;
          }
          counted++;
          if (Math.max(r, g, b) - Math.min(r, g, b) >= COLOUR_SATURATION_MIN) {
            coloured++;
          }
        }
        resolve(counted > 0 && coloured / counted >= COLOUR_PIXEL_FRACTION);
      } catch {
        // Tainted canvas (no CORS headers on the media host) — unknowable, not "no".
        resolve(null);
      }
    };

    if (/\.webm$/i.test(url)) {
      const v = document.createElement('video');
      v.crossOrigin = 'anonymous';
      v.muted = true;
      v.preload = 'auto';
      v.src = url;
      const onReady = () => {
        // Mid-clip: colour box may not be painted on the very first frame.
        const seekTo = Number.isFinite(v.duration) && v.duration > 0 ? v.duration / 2 : 0;
        const grab = () => finish(v, v.videoWidth, v.videoHeight);
        if (seekTo > 0) {
          v.onseeked = grab;
          v.currentTime = seekTo;
        } else {
          grab();
        }
      };
      v.onloadeddata = onReady;
      v.onerror = () => resolve(null);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => finish(img, img.naturalWidth, img.naturalHeight);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

const StudyOverview = ({ open, onClose, instances, getPlaybackRate }: StudyOverviewProps) => {
  // slot key → instance url
  const [assigned, setAssigned] = useState<Record<string, string>>({});
  const [colourByUrl, setColourByUrl] = useState<Record<string, boolean | null>>({});
  const [pickingSlot, setPickingSlot] = useState<string | null>(null);
  // slot key → true while the clip in it is an unconfirmed protocol-order suggestion.
  const [suggested, setSuggested] = useState<Record<string, boolean>>({});
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});

  const applyRate = (el: HTMLVideoElement | null, url: string) => {
    if (!el || !getPlaybackRate) {
      return;
    }
    const rate = getPlaybackRate(url);
    if (typeof rate === 'number' && rate > 0) {
      el.playbackRate = rate;
    }
  };

  // Colour is probed once per clip, lazily, and only while the screen is open.
  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    (async () => {
      for (const inst of instances) {
        if (cancelled) {
          return;
        }
        if (inst.url in colourByUrl) {
          continue;
        }
        const result = await detectColour(inst.url);
        if (cancelled) {
          return;
        }
        setColourByUrl(prev => ({ ...prev, [inst.url]: result }));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, instances]);

  /**
   * Fill what can be filled and leave the rest honestly empty.
   *
   * Auto-fill only ever uses the DICOM View Name plus the measured colour flag. Routine
   * echo studies carry NO view tag (0008,2127 is absent on GE Vivid output), so on those
   * studies this legitimately fills nothing and every slot is assigned by hand. Guessing
   * from clip order was considered and rejected: a mislabelled view is worse than a blank
   * one, because the cardiologist would read the wrong anatomy under a confident label.
   */
  const autoAssign = useCallback(() => {
    const taken = new Set<string>();
    const byTag: Record<string, string> = {};

    // ---- Pass 1: the DICOM View Name. Exact, trusted, never a guess. ----------
    for (const slot of SLOTS) {
      const match = instances.find(inst => {
        if (taken.has(inst.url) || !hasView(inst)) {
          return false;
        }
        const view = (inst.view as string).toUpperCase();
        if (!slot.tags.some(t => view === t.toUpperCase())) {
          return false;
        }
        if (slot.needsColour) {
          return colourByUrl[inst.url] === true;
        }
        return colourByUrl[inst.url] !== true;
      });
      if (match) {
        byTag[slot.key] = match.url;
        taken.add(match.url);
      }
    }

    /**
     * ---- Pass 2: protocol order, for the slots pass 1 could not fill. ----------
     *
     * Routine echo from these carts carries NO view label, so pass 1 fills nothing on
     * those studies and the grid would sit empty. The one real signal left is the order
     * the sonographer acquired in: a TTE is a fixed sweep — parasternal long axis, the
     * same with colour, short axis at the aortic valve, short axis at mid, then the
     * apical windows, then subcostal and IVC. `SLOTS` is written in that order, so
     * walking the clips in acquisition order and taking the next one that matches each
     * slot's colour requirement reproduces the sweep.
     *
     * These are SUGGESTIONS and are surfaced as such — an unconfirmed suggestion is drawn
     * in amber and marked, and never counts toward the confirmed total. A wrong view under
     * a confident label is worse than an empty panel; a wrong view under "suggested",
     * one click from being corrected, is a time-saver.
     */
    const ordered = [...instances].sort((a, b) => {
      const sa = Number(a.seriesNumber) || 0;
      const sb = Number(b.seriesNumber) || 0;
      if (sa !== sb) {
        return sa - sb;
      }
      const ia = Number(a.instanceNumber) || 0;
      const ib = Number(b.instanceNumber) || 0;
      if (ia !== ib) {
        return ia - ib;
      }
      // Stable fallback so the suggestion never reshuffles between renders.
      return instances.indexOf(a) - instances.indexOf(b);
    });

    const guessed: Record<string, string> = {};
    for (const slot of SLOTS) {
      if (byTag[slot.key]) {
        continue;
      }
      const match = ordered.find(inst => {
        if (taken.has(inst.url)) {
          return false;
        }
        const colour = colourByUrl[inst.url];
        /**
         * The colour probe answers true, false, or NULL — null meaning it could not tell
         * (the media host returns no CORS header, so reading the pixels back taints the
         * canvas). The two kinds of slot treat that unknown differently, and deliberately:
         *
         * - a COLOUR slot needs colour proven, so unknown is not good enough; leaving it
         *   empty is honest and the doctor fills it in one click.
         * - a GREYSCALE slot treats unknown as acceptable. Most clips in a study are
         *   greyscale, so this is the likely case, and the alternative — requiring proof
         *   of absence — left every panel empty whenever the probe could not run at all.
         *
         * Every placement here is a SUGGESTION shown in amber for confirmation, so the
         * cost of being wrong is one click, while the cost of the old strictness was an
         * entirely blank screen.
         */
        return slot.needsColour ? colour === true : colour !== true;
      });
      if (match) {
        guessed[slot.key] = match.url;
        taken.add(match.url);
      }
    }

    setAssigned(prev => ({ ...byTag, ...guessed, ...prev }));
    setSuggested(prev => {
      const next: Record<string, boolean> = { ...prev };
      for (const k of Object.keys(guessed)) {
        // Only mark as suggested if the doctor has not already settled that slot.
        if (!(k in prev)) {
          next[k] = true;
        }
      }
      for (const k of Object.keys(byTag)) {
        next[k] = false;
      }
      return next;
    });
  }, [instances, colourByUrl]);

  // Re-run as colour results land, but never overwrite a manual choice.
  useEffect(() => {
    if (open) {
      autoAssign();
    }
  }, [open, autoAssign]);

  const filledCount = SLOTS.filter(s => assigned[s.key]).length;
  const suggestedCount = SLOTS.filter(s => assigned[s.key] && suggested[s.key]).length;
  const confirmedCount = filledCount - suggestedCount;

  const confirmSlot = (slotKey: string) => setSuggested(prev => ({ ...prev, [slotKey]: false }));

  const confirmAll = () =>
    setSuggested(prev => {
      const next = { ...prev };
      for (const slot of SLOTS) {
        if (assigned[slot.key]) {
          next[slot.key] = false;
        }
      }
      return next;
    });

  const instanceFor = (slotKey: string) => {
    const url = assigned[slotKey];
    return url ? instances.find(i => i.url === url) : undefined;
  };

  const assignToSlot = (slotKey: string, url: string) => {
    setAssigned(prev => {
      const next = { ...prev };
      // One clip occupies one slot — moving it out of an old slot avoids duplicates.
      for (const k of Object.keys(next)) {
        if (next[k] === url) {
          delete next[k];
        }
      }
      next[slotKey] = url;
      return next;
    });
    // Choosing by hand settles the slot — it is no longer a machine suggestion.
    setSuggested(prev => ({ ...prev, [slotKey]: false }));
    setPickingSlot(null);
  };

  const renderClip = (url: string, slotKey: string) => {
    if (/\.webm$/i.test(url)) {
      return (
        <video
          key={url}
          ref={el => (videoRefs.current[slotKey] = el)}
          autoPlay
          loop
          muted
          playsInline
          onLoadedData={e => applyRate(e.currentTarget, url)}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        >
          <source
            src={url}
            type="video/webm"
          />
        </video>
      );
    }
    return (
      <img
        src={url}
        alt={slotKey}
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    );
  };

  const thumbFor = (inst: OverviewInstance) =>
    /\.webm$/i.test(inst.url) ? (
      <video
        src={inst.url}
        muted
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    ) : (
      <img
        src={inst.url}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: NAVY, backgroundImage: 'none' } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.25,
          bgcolor: PANEL,
          borderBottom: `1px solid ${HAIRLINE}`,
        }}
      >
        <AutoAwesomeMosaicRounded sx={{ color: ACCENT }} />
        <Typography sx={{ color: '#e2e8f0', fontWeight: 700, fontSize: 15 }}>
          Study overview
        </Typography>
        <Chip
          size="small"
          label={`${confirmedCount}/${SLOTS.length} confirmed`}
          sx={{
            bgcolor: confirmedCount === SLOTS.length ? ACCENT : 'rgba(148,163,184,0.16)',
            color: confirmedCount === SLOTS.length ? NAVY : '#cbd5e1',
            fontWeight: 700,
          }}
        />
        {suggestedCount > 0 && (
          <>
            <Chip
              size="small"
              label={`${suggestedCount} suggested`}
              sx={{ bgcolor: SUGGEST, color: NAVY, fontWeight: 700 }}
            />
            <Tooltip title="Accept every suggestion">
              <Box
                component="button"
                type="button"
                onClick={confirmAll}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.2,
                  py: 0.4,
                  borderRadius: 5,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#cbd5e1',
                  bgcolor: 'transparent',
                  border: `1px solid ${HAIRLINE}`,
                  '&:hover': { borderColor: ACCENT, color: ACCENT },
                }}
              >
                <DoneAllRounded sx={{ fontSize: 15 }} />
                Confirm all
              </Box>
            </Tooltip>
          </>
        )}
        <Typography sx={{ color: '#7c93ac', fontSize: 12, ml: 1 }}>
          {suggestedCount > 0
            ? 'Amber panels are suggested from the order the study was recorded in — check each one'
            : confirmedCount < SLOTS.length
              ? 'Click an empty panel to choose its clip'
              : 'Every view confirmed'}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ ml: 'auto', color: '#94a3b8' }}
          aria-label="Close overview"
        >
          <CloseRounded />
        </IconButton>
      </Box>

      {/* 3 × 3 — fixed, so each view always sits in the same place */}
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gridTemplateRows: 'repeat(3, 1fr)',
          gap: 1,
          p: 1,
          minHeight: 0,
        }}
      >
        {SLOTS.map(slot => {
          const inst = instanceFor(slot.key);
          const isSuggested = !!inst && !!suggested[slot.key];
          return (
            <Box
              key={slot.key}
              sx={{
                position: 'relative',
                bgcolor: TILE_BG,
                border: isSuggested ? `1px solid ${SUGGEST}` : `1px solid ${HAIRLINE}`,
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 0.9,
                  py: 0.4,
                  borderBottom: `1px solid ${HAIRLINE}`,
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: isSuggested ? SUGGEST : '#e2e8f0' }}
                >
                  {slot.label}
                </Typography>
                <Typography
                  sx={{ fontSize: 10.5, color: '#7c93ac' }}
                  noWrap
                >
                  {slot.detail}
                </Typography>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.25 }}>
                  {isSuggested && (
                    <Tooltip title="This is correct — confirm it">
                      <IconButton
                        size="small"
                        onClick={() => confirmSlot(slot.key)}
                        sx={{ color: SUGGEST, p: 0.25 }}
                        aria-label={`Confirm ${slot.label}`}
                      >
                        <CheckRounded sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={inst ? 'Change clip' : 'Choose clip'}>
                    <IconButton
                      size="small"
                      onClick={() => setPickingSlot(slot.key)}
                      sx={{ color: '#94a3b8', p: 0.25 }}
                      aria-label={`Choose clip for ${slot.label}`}
                    >
                      <EditRounded sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                  {inst && (
                    <IconButton
                      size="small"
                      onClick={() =>
                        setAssigned(prev => {
                          const next = { ...prev };
                          delete next[slot.key];
                          return next;
                        })
                      }
                      sx={{ color: '#94a3b8', p: 0.25 }}
                      aria-label={`Clear ${slot.label}`}
                    >
                      <ClearRounded sx={{ fontSize: 15 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>

              <Box
                onClick={() => !inst && setPickingSlot(slot.key)}
                sx={{
                  position: 'relative',
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 0,
                  cursor: inst ? 'default' : 'pointer',
                }}
              >
                {inst ? (
                  <>
                    {renderClip(inst.url, slot.key)}
                    {isSuggested && (
                      <Typography
                        sx={{
                          position: 'absolute',
                          top: 6,
                          left: 6,
                          px: 0.8,
                          py: 0.15,
                          borderRadius: 5,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.05em',
                          color: NAVY,
                          bgcolor: SUGGEST,
                          pointerEvents: 'none',
                        }}
                      >
                        SUGGESTED
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography sx={{ fontSize: 12, color: '#475569' }}>Click to choose</Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Picker */}
      <Dialog
        open={!!pickingSlot}
        onClose={() => setPickingSlot(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: PANEL, backgroundImage: 'none' } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography sx={{ color: '#e2e8f0', fontWeight: 700, mb: 1.5 }}>
            {SLOTS.find(s => s.key === pickingSlot)?.label} — choose a clip
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
              gap: 1,
              maxHeight: '65vh',
              overflowY: 'auto',
            }}
          >
            {instances.map((inst, idx) => {
              const isColour = colourByUrl[inst.url];
              return (
                <Box
                  key={inst.url}
                  onClick={() => pickingSlot && assignToSlot(pickingSlot, inst.url)}
                  sx={{
                    position: 'relative',
                    height: 110,
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: `1px solid ${HAIRLINE}`,
                    '&:hover': { borderColor: ACCENT },
                  }}
                >
                  {thumbFor(inst)}
                  <Typography
                    sx={{
                      position: 'absolute',
                      left: 4,
                      bottom: 4,
                      px: 0.7,
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#cbd5e1',
                      bgcolor: 'rgba(6,18,34,0.7)',
                    }}
                  >
                    {hasView(inst) ? inst.view : `Clip ${idx + 1}`}
                  </Typography>
                  {isColour === true && (
                    <Typography
                      sx={{
                        position: 'absolute',
                        right: 4,
                        top: 4,
                        px: 0.7,
                        borderRadius: 4,
                        fontSize: 9.5,
                        fontWeight: 700,
                        color: NAVY,
                        bgcolor: ACCENT,
                      }}
                    >
                      COLOUR
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default StudyOverview;
